import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

type TrafficStatus = "green" | "amber" | "red" | "neutral";

interface TrafficLightRevealProps {
  status: TrafficStatus;
  delay?: number;
  size?: "sm" | "md" | "lg";
  showPulse?: boolean;
  className?: string;
}

export function TrafficLightReveal({
  status,
  delay = 0,
  size = "md",
  showPulse = false,
  className,
}: TrafficLightRevealProps) {
  const [revealed, setRevealed] = useState(false);
  const [pulsing, setPulsing] = useState(false);

  useEffect(() => {
    const revealTimer = setTimeout(() => {
      setRevealed(true);
      
      if (showPulse && status === "red") {
        // Add pulse effect for red items
        const pulseTimer = setTimeout(() => {
          setPulsing(true);
        }, 300);
        return () => clearTimeout(pulseTimer);
      }
    }, delay);

    return () => clearTimeout(revealTimer);
  }, [delay, showPulse, status]);

  const sizeClasses = {
    sm: "h-2 w-2",
    md: "h-3 w-3",
    lg: "h-4 w-4",
  };

  const statusClasses = {
    green: "bg-success",
    amber: "bg-warning",
    red: "bg-danger",
    neutral: "bg-muted-foreground",
  };

  const glowClasses = {
    green: "shadow-[0_0_8px_2px_hsl(var(--success)/0.4)]",
    amber: "shadow-[0_0_8px_2px_hsl(var(--warning)/0.4)]",
    red: "shadow-[0_0_12px_3px_hsl(var(--danger)/0.5)]",
    neutral: "",
  };

  return (
    <div
      className={cn(
        "rounded-full transition-all duration-300 ease-out",
        sizeClasses[size],
        revealed ? statusClasses[status] : "bg-muted scale-75 opacity-50",
        revealed && glowClasses[status],
        pulsing && status === "red" && "animate-pulse",
        className
      )}
      style={{
        transitionDelay: revealed ? "0ms" : `${delay}ms`,
      }}
    />
  );
}

// Group of traffic lights with staggered reveal
interface TrafficLightGroupProps {
  items: {
    status: TrafficStatus;
    label: string;
    value?: string | number;
  }[];
  staggerDelay?: number;
  className?: string;
}

export function TrafficLightGroup({
  items,
  staggerDelay = 150,
  className,
}: TrafficLightGroupProps) {
  return (
    <div className={cn("space-y-2", className)}>
      {items.map((item, index) => (
        <div
          key={index}
          className="flex items-center gap-3 animate-in-up"
          style={{ animationDelay: `${index * staggerDelay}ms` }}
        >
          <TrafficLightReveal
            status={item.status}
            delay={index * staggerDelay}
            showPulse={item.status === "red"}
          />
          <span className="text-sm font-medium">{item.label}</span>
          {item.value !== undefined && (
            <span className="ml-auto text-sm text-muted-foreground tabular-nums">
              {item.value}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// Emoji variant for chat messages
interface TrafficEmojiProps {
  status: TrafficStatus;
  delay?: number;
}

export function TrafficEmoji({ status, delay = 0 }: TrafficEmojiProps) {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setRevealed(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const emojiMap = {
    green: "🟢",
    amber: "🟡",
    red: "🔴",
    neutral: "⚪",
  };

  return (
    <span
      className={cn(
        "inline-block transition-all duration-300",
        revealed ? "opacity-100 scale-100" : "opacity-0 scale-50"
      )}
    >
      {emojiMap[status]}
    </span>
  );
}
